import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  useAddLoanerAgent,
  useLoanerAgents,
  useLoanerDirectory,
  useRevokeLoanerAgent,
  useSetLoanerEmail,
} from "../api/hooks";
import { EditableEmail, Initials, RowActions, statusRank, StatusBadge } from "./AdminUI";
import { ImpersonateLoanerCard } from "./ImpersonateLoanerCard";

const PAGE_SIZE = 50;

/** Full låntaker (borrower) directory — all synced loaners, registered or not. Paginated
 *  client-side (the list is small) so it matches the långiver table exactly. */
export function LoanersTab() {
  const navigate = useNavigate();
  const { data: loaners, isLoading, isError } = useLoanerDirectory();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [openAgents, setOpenAgents] = useState<number | null>(null);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      (loaners ?? [])
        .filter(
          (l) =>
            !q ||
            l.name?.toLowerCase().includes(q) ||
            l.orgNumber?.toLowerCase().includes(q) ||
            l.email?.toLowerCase().includes(q)
        )
        .sort(
          (a, b) =>
            statusRank(a.status) - statusRank(b.status) ||
            (a.name ?? "").localeCompare(b.name ?? "", "nb-NO")
        ),
    [loaners, q]
  );

  // Reset to the first page whenever the search narrows the list.
  useEffect(() => setPage(0), [q]);

  const total = filtered.length;
  const start = page * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + PAGE_SIZE, total);

  return (
    <div>
      <ImpersonateLoanerCard />
      <SearchBox value={query} onChange={setQuery} placeholder="Søk på navn, org.nr eller e-post" />

      {isLoading ? (
        <Centered>
          <Loader2 className="size-5 animate-spin" /> Laster låntakere…
        </Centered>
      ) : isError ? (
        <ErrorBox>Kunne ikke laste låntakere.</ErrorBox>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[760px] text-left text-sm">
            <Thead cols={["#", "Selskap", "Org.nr", "Kontakt / e-post", "Lån", "Status", "Handling"]} />
            <tbody className="divide-y divide-white/5">
              {pageRows.flatMap((l, i) => [
                <tr key={l.id} className="transition-colors hover:bg-white/[0.04]">
                  <RowNum n={start + i + 1} />
                  <td className="px-4 py-2.5">
                    {l.orgNumber ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/selskapsinfo/${l.orgNumber}`)}
                        title="Åpne selskapsinfo"
                        className="flex items-center gap-3 text-left transition-colors hover:text-teal-200"
                      >
                        <Initials name={l.name} fallback={l.orgNumber ?? "?"} />
                        <span className="font-medium text-white hover:text-teal-200">{l.name ?? "—"}</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Initials name={l.name} fallback={l.orgNumber ?? "?"} />
                        <span className="font-medium text-white">{l.name ?? "—"}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-white/60">{l.orgNumber ?? "—"}</td>
                  <td className="px-4 py-2.5 text-white/70">
                    <div>{l.contactPerson ?? "—"}</div>
                    <LoanerEmail id={l.id} email={l.email} />
                    <AgentsToggle
                      open={openAgents === l.id}
                      onClick={() => setOpenAgents((cur) => (cur === l.id ? null : l.id))}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-white/70">{l.loanCount}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="px-4 py-2.5">
                    <RowActions accountId={l.accountId} status={l.status} />
                  </td>
                </tr>,
                openAgents === l.id ? (
                  <tr key={`${l.id}-agents`}>
                    <td colSpan={7} className="bg-white/[0.02] px-4 py-3">
                      <LoanerAgentsPanel loanerId={l.id} />
                    </td>
                  </tr>
                ) : null,
              ])}
              {pageRows.length === 0 && <EmptyRow cols={7} label="Ingen låntakere funnet." />}
            </tbody>
          </table>
        </div>
      )}

      <Pager
        from={from}
        to={to}
        total={total}
        hasPrev={page > 0}
        hasNext={to < total}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}

/** A loaner's editable on-file email (admin override). */
function LoanerEmail({ id, email }: { id: number; email: string | null }) {
  const setEmail = useSetLoanerEmail();
  return (
    <EditableEmail
      email={email}
      saving={setEmail.isPending}
      onSave={(e) => setEmail.mutateAsync({ id, email: e })}
    />
  );
}

/** Toggle under the contact cell that opens the loaner's extra-contacts panel. */
function AgentsToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 flex items-center gap-1 text-xs font-medium text-white/45 transition-colors hover:text-teal-200"
    >
      <Users className="size-3" />
      Kontakter
      <ChevronDown className={cn("size-3 transition-transform", open && "rotate-180")} />
    </button>
  );
}

/** Expandable panel: lists the loaner's extra contact people (loaner_agents) and lets the
 *  admin add/remove them so each person can register their own account under the company. */
function LoanerAgentsPanel({ loanerId }: { loanerId: number }) {
  const { data: agents, isLoading } = useLoanerAgents(loanerId);
  const revoke = useRevokeLoanerAgent(loanerId);

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wider text-white/40">
        Ekstra kontakter / e-poster
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-sm text-white/50">
          <Loader2 className="size-4 animate-spin" /> Laster kontakter…
        </div>
      ) : agents && agents.length > 0 ? (
        <ul className="space-y-1.5">
          {agents.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
            >
              <Initials name={a.name} fallback={a.email} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-white">{a.name ?? a.email}</div>
                <div className="truncate text-xs text-white/45">
                  {a.email}
                  {a.phone ? ` · ${a.phone}` : ""}
                </div>
                {a.alsoCompanies?.length ? (
                  <span className="mt-1 inline-flex items-center rounded-full bg-teal-400/15 px-2.5 py-0.5 text-xs font-medium text-teal-200 ring-1 ring-inset ring-teal-400/30">
                    Også: {a.alsoCompanies.join(", ")}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                disabled={revoke.isPending && revoke.variables === a.id}
                onClick={() => revoke.mutate(a.id)}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-white/15 px-3 text-xs font-medium text-white/70 transition-colors hover:bg-white/5 disabled:opacity-40"
              >
                {revoke.isPending && revoke.variables === a.id ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Trash2 className="size-3.5" />
                )}
                Fjern
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="py-1 text-sm text-white/40">Ingen ekstra kontakter.</div>
      )}

      <AddAgentForm loanerId={loanerId} />
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Add-contact form: e-post (required), navn + telefon (optional). Backend re-validates. */
function AddAgentForm({ loanerId }: { loanerId: number }) {
  const add = useAddLoanerAgent(loanerId);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const e = email.trim();
    if (!EMAIL_RE.test(e)) {
      setError("Oppgi en gyldig e-postadresse");
      return;
    }
    setError(null);
    try {
      await add.mutateAsync({
        email: e,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      setEmail("");
      setName("");
      setPhone("");
    } catch {
      setError("Kunne ikke legge til kontakten.");
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="E-post"
          className="h-8 w-56 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
        />
        <input
          type="text"
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          placeholder="Navn"
          className="h-8 w-40 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
        />
        <input
          type="tel"
          value={phone}
          onChange={(ev) => setPhone(ev.target.value)}
          placeholder="Telefon"
          className="h-8 w-36 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={add.isPending || !email.trim()}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-teal-400 px-3 text-xs font-semibold text-[#04231d] transition-opacity hover:bg-teal-300 disabled:opacity-40"
        >
          {add.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          Legg til
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-300">{error}</div>}
    </div>
  );
}

// --- shared table chrome (used by both directory tables) --------------------

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative mb-4 max-w-sm">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-white/40" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-xl border border-white/10 bg-white/[0.04] pl-10 pr-3 text-sm text-white placeholder:text-white/40 focus:border-teal-400/50 focus:outline-none"
      />
    </div>
  );
}

export function Thead({ cols }: { cols: string[] }) {
  return (
    <thead className="border-b border-white/10 bg-white/[0.03] text-xs uppercase tracking-wide text-white/45">
      <tr>
        {cols.map((c, i) => (
          <th
            key={c}
            className={cn(
              "px-4 py-2.5 font-medium",
              i === cols.length - 1 && "text-right",
              c === "#" && "w-12 text-center"
            )}
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** Leading row-number cell (continuous across pages). */
export function RowNum({ n }: { n: number }) {
  return (
    <td className="w-12 px-4 py-2.5 text-center text-xs tabular-nums text-white/35">{n}</td>
  );
}

export function EmptyRow({ cols, label }: { cols: number; label: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-white/40">
        {label}
      </td>
    </tr>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2 py-16 text-sm text-white/50">{children}</div>;
}

export function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
      {children}
    </div>
  );
}

/** Footer with "Viser X–Y av Z" + prev/next arrows. Shared by both directory tables. */
export function Pager({
  from,
  to,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  isFetching,
}: {
  from: number;
  to: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  isFetching?: boolean;
}) {
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-white/45">
      <span className="flex items-center gap-2">
        {total > 0 ? `Viser ${from}–${to} av ${total.toLocaleString("nb-NO")}` : "—"}
        {isFetching && <Loader2 className="size-3 animate-spin" />}
      </span>
      <div className="flex items-center gap-1">
        <PageButton disabled={!hasPrev} onClick={onPrev}>
          <ChevronLeft className="size-4" />
        </PageButton>
        <PageButton disabled={!hasNext} onClick={onNext}>
          <ChevronRight className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

export function PageButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-lg border border-white/10 text-white/70 transition-colors hover:bg-white/5 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
